import AppConfig from '../appConfig';
import AbstractFetch from './AbstractFetch';
import Helper from '../lib/Helper';

import originalFetch from 'isomorphic-fetch';
import fetchRetry from 'fetch-retry';

class ZywaveProxyFetch {
  /**
   * Wraps the standard fetch method.
   *
   * Adds automatic retries for GET requests.
   * When response is abnormal then automatically retry
   *
   * @param url: The request url.
   * @param options: The request options.
   */
  static fetch = async (url, options, retry = false) => {
    const defaultFetchOptions = {
      method: 'GET',
      cache: 'force-cache', // Must lookup cache before make request to remote
      retries: 2,
      retryDelay: 2000,
    };
    const defaultRequestHeaders = {
      'Cache-Control': 'public, max-age=1200, immutable',
      Pragma: 'public, max-age=1200, immutable',
    };

    let fetchOptions = Object.assign({}, defaultFetchOptions, options);

    if (fetchOptions.method === 'GET') {
      fetchOptions.headers = Object.assign(
        {},
        defaultRequestHeaders,
        options['headers'] ? options['headers'] : {}
      );
    }

    const fetchFunc =
      options && (options.method === 'GET' || retry) ? fetchRetry(originalFetch) : window.fetch;

    if (fetchOptions.method === 'GET' && !fetchOptions.retryOn) {
      // Note: Setting retryOn causes fetch-retry to reject the promise after all retries are exhausted.
      fetchOptions.retryOn = [401, 500, 502, 503, 504];
    }
    // ensure shell and proxy are defined before making the request
    await customElements.whenDefined('zywave-shell');
    await customElements.whenDefined('zywave-api-proxy');

    return fetchFunc(url, fetchOptions).then((response) => {
      if (response.ok) {
        return response;
      }

      throw Error(`HTTP ${response.status}. ${response.text}`);
    });
  };

  static handleErrors = () => {
    return false;
  };

  /**
   * Perform escape given text for OData search (Zywave API).
   * It replaces single apostrophe (') by two apostrophes ('')
   * @param {*} error
   * @returns escaped string
   */
  static escapeOData = (text) => {
    if (!text) {
      return text;
    }
    return String(text).replaceAll("'", "''");
  };

  /**
   * Search agencies having first name or last name containing the given keyword `name`
   * In case the results have more than 1 page, it will attempt to fetch all page before returning.
   *
   * @param name - the name to search
   * @return json|null = results of the API call to https://qa-api.zywave.com/proxy/agencies/v2.0/agencyuser
   *  {
   *      "hasMoreResults": false,
   *      "pageIndex": 0,
   *      "pageSize": 100,
   *      "skip": 0,
   *      "take": 100,
   *      "totalCount": 1,
   *      "items": [
   *          {
   *              "id": 875737,
   *              "agencyId": 103484,
   *              "createdDateTime": null,
   *              "createdUserPrincipalId": null,
   *              "updatedDateTime": null,
   *              "updatedUserPrincipalId": null,
   *              "biography": null,
   *              "designation": null,
   *              "emailAddress": "Long.Nguyen@zywave.com",
   *              "externalEmployeeId": null,
   *              "faxNumber": null,
   *              "firstName": "Long",
   *              "isAdministrator": false,
   *              "isEnabled": true,
   *              "lastName": "Nguyen",
   *              "linesOfBusiness": ["Benefits",  "PropertyCasualty", "PersonalLines",  "Financial"],
   *              "middleInitial": null,
   *              "npn": null,
   *              "phoneNumber": null,
   *              "primaryRole": "InformationTechnology",
   *              "salutation": null,
   *              "title": null
   *          }
   *      ]
   *  }
   */

  static async searchAgencies(name) {
    // ensure shell and proxy are defined before making the request
    await customElements.whenDefined('zywave-shell');
    await customElements.whenDefined('zywave-api-proxy');

    const pageSize = 100;
    const fetchPage = (name, pageIndex) => {
      let criterias = ['isEnabled eq true'];
      if (name && name.trim() !== '') {
        criterias.push(
          "(contains(firstName, '" +
            name.trim() +
            "') or contains(lastName, '" +
            name.trim() +
            "'))"
        );
      }

      // Call to self implemented fetch function
      return this.fetch(
        AppConfig.zywaveProxyUrl +
          'agencies/v2.0/agencyuser?$orderBy=' +
          encodeURIComponent('lastName asc') +
          '&$filter=' +
          encodeURIComponent(criterias.join(' and ')) +
          '&$skip=' +
          String(pageIndex * pageSize) +
          '&$top=' +
          String(pageSize),
        {
          method: 'GET',
          credentials: 'include',
        }
      ).then((response) => {
        return response ? response.json() : null;
      });
    };

    return fetchPage(name, 0).then((json) => {
      if (!json || json['hasMoreResults'] === false) {
        return json;
      }

      // Case of multi-pages
      const totalCount = parseInt(json['totalCount']);
      const numOfPages =
        totalCount % pageSize === 0
          ? Math.floor(totalCount / pageSize)
          : Math.floor(totalCount / pageSize) + 1;
      if (numOfPages <= 1) {
        return json;
      }

      return new Promise((resolve, reject) => {
        // Fetch N pages
        Promise.allSettled(
          Array(numOfPages)
            .fill(1)
            .map((v, i) => fetchPage('', i))
        ).then((values) => {
          const fulfilledObjects = values.filter((v) => v.status === 'fulfilled');
          if (!fulfilledObjects || fulfilledObjects.length === 0) {
            reject('Fetch agencies/v2.0/agencyuser all failed.');
          }

          let allItems = [];
          fulfilledObjects.forEach((object) => {
            object.value['items'].forEach((item) => allItems.push(item));
          });

          let combinedResponse = fulfilledObjects[0].value;
          combinedResponse['items'] = allItems;
          combinedResponse['hasMoreResults'] = false;

          resolve(combinedResponse);
        });
      });
    });
  }

  /**
   * Retrieve a dictionary of feature and license data for the active profile
   * @return json|null
   * {
   *   <any-key>: {
   *     Description: string|null     * The feature description.
   *     Id: integer     * The feature identifier.
   *     IsEnabled: boolean     * Specifies whether the feature is enabled.
   *     Key: guid     * The unique feature key.
   *     Name: string|null     * The feature name.
   *     Tags: [string]     * All tags associated with the feature.
   *     Licenses: [     * All licenses that have granted the feature.
   *       {
   *         EndDateTime: date-time|null     * The effective end time for the license.
   *         Id: integer     * The license identifier.
   *         Name: string|null     * The license name.
   *         Quantity: integer     * The quantity supplied by the license, if applicable.
   *         StartDateTime: date-time     * The effective start time for the license.
   *       },
   *     ]
   *   },
   * }
   */
  static async getFeatureLicense() {
    // ensure shell and proxy are defined before making the request
    await customElements.whenDefined('zywave-shell');
    await customElements.whenDefined('zywave-api-proxy');

    // Call to self implemented fetch function
    return this.fetch(AppConfig.zywaveProxyUrl + 'licenses/v2.0/featurelicense', {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => {
        return response ? response.json() : null;
      })
      .catch(this.handleErrors);
  }

  static async getZywaveUserInfo() {
    try {
      // ensure shell and proxy are defined before making the request
      await customElements.whenDefined('zywave-shell');
      await customElements.whenDefined('zywave-api-proxy');
      const response = await fetch(AppConfig.zywaveProxyUrl + 'userinfo', {
        credentials: 'include',
        method: 'GET',
        cache: 'no-cache',
      });
      return response;
    } catch (result) {
      return this.handleErrors(result);
    }
  }

  /**
   * Find existing AMS accounts that match the given information
   * @param filter json. A object represent criterias to be matched. It supports keys such as:
   *   msid, name, addressLine1, addressLine2, state, postalCode, phoneNumber, classification, updatedDateTime, industryId
   *
   * @return json
   *   {
   *       hasMoreResults: boolean
   *       pageIndex: integer
   *       pageSize: integer
   *       skip: integer
   *       take: integer
   *       totalCount: integer
   *       items: [
   *         {
   *              isArchived: boolean
   *              name*: string
   *              addressLine1: string┃null
   *              addressLine2: string┃null
   *              Constraints: Max 255 chars
   *              addressLine3: string┃null
   *              city: string┃null
   *              county: string┃null
   *              Constraints:
   *              state: string┃null
   *              country: string┃null
   *              postalCode: string┃null
   *              phoneNumber: string┃null
   *              ...
   *              msid: string┃null
   *          },
   *      ]
   *   }
   */
  static async getAccountMatches(filter) {
    let criteria_text = '';
    let criterias1 = [];
    let criterias2 = [];
    let criterias3 = [];

    if (Object.prototype.hasOwnProperty.call(filter, 'msid') && filter.msid) {
      criterias1.push("msid eq '" + String(filter.msid) + "'");
    }

    if (Object.prototype.hasOwnProperty.call(filter, 'name') && filter.name) {
      criterias2.push("contains(name, '" + this.escapeOData(filter.name) + "')");
    }

    if (Object.prototype.hasOwnProperty.call(filter, 'addressLine1') && filter.addressLine1) {
      criterias2.push("contains(addressLine1, '" + this.escapeOData(filter.addressLine1) + "')");
    }

    if (Object.prototype.hasOwnProperty.call(filter, 'phoneNumber') && filter.phoneNumber) {
      const phoneNumber = filter.phoneNumber;
      let phoneNumberFormat = Helper.formatPhoneAms(phoneNumber);
      if (phoneNumber.length > 10) {
        phoneNumberFormat +=
          "','" + Helper.formatPhoneAms(phoneNumber.slice(1, phoneNumber.length));
      }
      criterias3.push("PhoneNumber in ('" + String(phoneNumberFormat) + "')");
    }

    if (criterias1.length > 0 && criterias2.length > 0) {
      criteria_text = criterias1.join(' and ') + ' or ' + criterias2.join(' and ');
      if (criterias3.length > 0) {
        criteria_text += ' or ' + criterias3.join(' and ');
      }
    } else if (criterias1.length > 0) {
      criteria_text = criterias1.join(' and ');
    } else if (criterias2.length > 0) {
      criteria_text = criterias2.join(' or ');
    } else if (criterias3.length > 0) {
      criteria_text = criterias3.join(' and ');
    }

    // Call to common fetch function
    return AbstractFetch.fetch(
      AppConfig.zywaveProxyUrl +
        'accounts/v2.0/account/matches?$filter=' +
        encodeURIComponent(criteria_text) +
        '&$orderBy=name',
      {
        method: 'GET',
        credentials: 'include',
        cache: 'force-cache', // Must lookup cache before make request to remote
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
        },
      },
      true
    )
      .then((response) => {
        return response.json();
      })
      .catch(this.handleErrors);
  }

  /**
   * list account contacts in accountIds
   * @param filter json. A object represent criterias to be matched. It supports keys such as:
   *   accountIds
   *
   * @return json
   *   {
   *       hasMoreResults: boolean
   *       pageIndex: integer
   *       pageSize: integer
   *       skip: integer
   *       take: integer
   *       totalCount: integer
   *       items: [
   *         {
   *          accountId: integer
   *          addressLine1: string┃null
   *          addressLine2: string┃null
   *          addressLine3: string┃null
   *          birthDate: date-time┃null
   *          city: string┃null
   *          contactConsentExpirationDateTime: date-time┃null
   *          contactConsentSource: string┃null
   *          ...
   *          phones: [..{..}}]
   *         },
   *      ]
   *   }
   */
  static async getAccountContactByIds(filter) {
    let criteria_text = '';
    let criterias1 = [];

    if (Object.prototype.hasOwnProperty.call(filter, 'accountIds') && filter.accountIds) {
      criterias1.push('accountId in (' + filter.accountIds.join() + ')');
    }

    if (criterias1.length > 0) {
      criteria_text = criterias1.join(' and ');
    }

    // Call to common fetch function
    return AbstractFetch.fetch(
      AppConfig.zywaveProxyUrl +
        'accounts/v2.0/accountcontact?$filter=' +
        encodeURIComponent(criteria_text),
      {
        method: 'GET',
        credentials: 'include',
        cache: 'force-cache', // Must lookup cache before make request to remote
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
        },
      },
      true
    )
      .then((response) => {
        return response.json();
      })
      .catch(this.handleErrors);
  }
}

export default ZywaveProxyFetch;
